import type { FormField, FormSchema } from "@/lib/database";
import { getGroupFields } from "@/lib/fields/ai-group";

/**
 * Правила ИИ-распознавания документов (kanban t_aba782d3).
 *
 * Ключевые ограничения:
 * - включать распознавание можно у поля «Файл» и вне групп, и внутри группы;
 * - поле ВНЕ группы заполняет ВСЕ поля формы, поле в группе — только поля своей группы;
 * - вне групп — не больше одного включённого поля на форму, в группе — не больше одного
 *   на группу, и поле вне групп взаимно исключает групповые (решение B, 2026-09-14).
 */

export const RECOGNITION_PROMPT_MAX_CHARS = 500;

/** Типы, которые распознавание не заполняет. */
const NON_FILLABLE_TYPES = new Set([
	"file",
	"statement",
	"banner",
	"signature",
]);

/** Включено ли у поля ИИ-распознавание документов. */
export const isRecognitionEnabled = (field: FormField): boolean =>
	field.type === "file" && field.settings?.recognition?.enabled === true;

/** Пользовательский промпт поля (дополняет системный). */
export const getRecognitionPrompt = (field: FormField): string =>
	(field.settings?.recognition?.customPrompt || "").trim();

/** Поля верхнего уровня: schema.blocks, а при их отсутствии — schema.fields. */
const getTopLevelFields = (schema?: FormSchema | null): FormField[] => {
	if (!schema) {
		return [];
	}
	return schema.blocks?.length
		? schema.blocks.flatMap((block) => block.fields || [])
		: schema.fields || [];
};

/** Группа полей, внутри которой лежит поле (null — поле вне групп). */
export const findParentGroup = (
	schema: FormSchema | null | undefined,
	fieldId: string
): FormField | null => {
	for (const field of getTopLevelFields(schema)) {
		if (
			field.type === "field-group" &&
			getGroupFields(field).some((groupField) => groupField.id === fieldId)
		) {
			return field;
		}
	}
	return null;
};

/** Найти поле по id: на верхнем уровне формы или внутри группы. */
export const findFieldInSchema = (
	schema: FormSchema | null | undefined,
	fieldId: string
): FormField | null => {
	for (const field of getTopLevelFields(schema)) {
		if (field.id === fieldId) {
			return field;
		}
		const nested = getGroupFields(field).find(
			(groupField) => groupField.id === fieldId
		);
		if (nested) {
			return nested;
		}
	}
	return null;
};

/** Поле той же группы (кроме исключённого) с включённым распознаванием. */
export const findEnabledRecognitionFieldInGroup = (
	group: FormField | null,
	excludeFieldId?: string
): FormField | null => {
	if (!group) {
		return null;
	}
	return (
		getGroupFields(group).find(
			(field) => field.id !== excludeFieldId && isRecognitionEnabled(field)
		) || null
	);
};

/** Включённое распознавание у поля ВНЕ групп (кроме исключённого). */
export const findEnabledRecognitionFieldOutsideGroups = (
	schema: FormSchema | null | undefined,
	excludeFieldId?: string
): FormField | null =>
	getTopLevelFields(schema).find(
		(field) =>
			field.type !== "field-group" &&
			field.id !== excludeFieldId &&
			isRecognitionEnabled(field)
	) || null;

/** Любое включённое распознавание в схеме — и вне групп, и внутри них. */
export const findEnabledRecognitionFieldAnywhere = (
	schema: FormSchema | null | undefined,
	excludeFieldId?: string
): FormField | null => {
	for (const field of getTopLevelFields(schema)) {
		if (field.id !== excludeFieldId && isRecognitionEnabled(field)) {
			return field;
		}
		const nested = findEnabledRecognitionFieldInGroup(field, excludeFieldId);
		if (nested) {
			return nested;
		}
	}
	return null;
};

/**
 * Состояние тоггла распознавания для конкретного поля: лежит ли поле в группе, какую
 * область оно заполняет (`fillsWholeForm` — поле вне групп) и какое другое поле с
 * включённым распознаванием мешает его включить.
 */
export const getRecognitionToggleState = (
	schema: FormSchema | null | undefined,
	field: FormField
): {
	blockedBy: FormField | null;
	fillsWholeForm: boolean;
	inGroup: boolean;
	parentGroup: FormField | null;
} => {
	const parentGroup = findParentGroup(schema, field.id);
	if (!parentGroup) {
		return {
			blockedBy: findEnabledRecognitionFieldAnywhere(schema, field.id),
			fillsWholeForm: true,
			inGroup: false,
			parentGroup: null,
		};
	}
	return {
		blockedBy:
			findEnabledRecognitionFieldOutsideGroups(schema, field.id) ??
			findEnabledRecognitionFieldInGroup(parentGroup, field.id),
		fillsWholeForm: false,
		inGroup: true,
		parentGroup,
	};
};

/**
 * Поля, которые заполняет распознавание для данного файлового поля:
 * - поле ВНЕ групп — ВСЕ поля формы (верхний уровень + поля всех групп);
 * - поле внутри группы — только поля этой группы.
 * Сам файл и нефункциональные блоки (statement/banner/signature) исключаются.
 */
export const getRecognitionFillableFields = (
	schema: FormSchema | null | undefined,
	fileFieldId: string
): FormField[] => {
	const parentGroup = findParentGroup(schema, fileFieldId);
	const scope = parentGroup
		? getGroupFields(parentGroup)
		: getTopLevelFields(schema).flatMap((field) =>
				field.type === "field-group" ? getGroupFields(field) : [field]
			);
	return scope.filter(
		(field) => field.id !== fileFieldId && !NON_FILLABLE_TYPES.has(field.type)
	);
};

const withRecognitionDisabled = (field: FormField): FormField => ({
	...field,
	settings: {
		...field.settings,
		recognition: {
			customPrompt: field.settings?.recognition?.customPrompt,
			enabled: false,
			requireValidation: field.settings?.recognition?.requireValidation ?? true,
		},
	},
});

/**
 * Приводит схему к правилам распознавания:
 * - вне групп — максимум одно включённое поле на форму;
 * - в группе — максимум одно включённое поле;
 * - поле вне групп взаимно исключает групповые (оно заполняет всю форму).
 *
 * При конфликте приоритет у `actorFieldId` (поле, которое только что изменили), иначе —
 * у первого по порядку. Возвращает исходный объект схемы, если менять нечего (важно для
 * ссылочной стабильности в конструкторе).
 */
export const normalizeRecognitionInSchema = (
	schema: FormSchema,
	actorFieldId?: string | null
): FormSchema => {
	const topLevel = getTopLevelFields(schema);
	const enabledOutside = topLevel.filter(isRecognitionEnabled);
	const enabledInGroups = topLevel
		.filter((field) => field.type === "field-group")
		.map((group) => getGroupFields(group).filter(isRecognitionEnabled));

	const disabledIds = new Set<string>();

	const disableOthers = (fields: FormField[], keepId?: string | null) => {
		for (const field of fields) {
			if (field.id !== keepId) {
				disabledIds.add(field.id);
			}
		}
	};
	const disableAllGroups = () => {
		for (const fields of enabledInGroups) {
			disableOthers(fields);
		}
	};

	const actorOutside = enabledOutside.some(
		(field) => field.id === actorFieldId
	);
	const actorGroupIndex = enabledInGroups.findIndex((fields) =>
		fields.some((field) => field.id === actorFieldId)
	);

	if (actorOutside) {
		// Только что включили поле вне групп — оно главнее остальных
		disableOthers(enabledOutside, actorFieldId);
		disableAllGroups();
	} else if (actorGroupIndex >= 0) {
		// Только что включили поле в группе — гасим верхнеуровневые и соседей по группе
		disableOthers(enabledOutside);
		disableOthers(enabledInGroups[actorGroupIndex], actorFieldId);
	} else if (enabledOutside.length > 0) {
		// Включённое поле вне групп одно на форму — групповые не работают
		disableOthers(enabledOutside, enabledOutside[0].id);
		disableAllGroups();
	} else {
		for (const fields of enabledInGroups) {
			disableOthers(fields, fields[0]?.id);
		}
	}

	if (disabledIds.size === 0) {
		return schema;
	}

	const normalizeFieldList = (fields: FormField[]): FormField[] =>
		fields.map((field) => {
			if (field.type === "field-group") {
				const groupFields = getGroupFields(field);
				const nextGroupFields = groupFields.map((groupField) =>
					disabledIds.has(groupField.id)
						? withRecognitionDisabled(groupField)
						: groupField
				);
				const changed = nextGroupFields.some(
					(groupField, index) => groupField !== groupFields[index]
				);
				return changed
					? {
							...field,
							settings: { ...field.settings, groupFields: nextGroupFields },
						}
					: field;
			}
			return disabledIds.has(field.id) ? withRecognitionDisabled(field) : field;
		});

	return {
		...schema,
		blocks: (schema.blocks || []).map((block) => ({
			...block,
			fields: normalizeFieldList(block.fields || []),
		})),
		fields: normalizeFieldList(schema.fields || []),
	};
};
